import { RefObject, useCallback, useEffect, useSyncExternalStore } from "react"
import { TIMELINE_ROW } from "../style/sizing"

/**
 * Single source of truth for the horizontal scroll state of the timeline.
 *
 * Replaces the former `useInViewport`, which registered one scroll listener AND one
 * `getBoundingClientRect()` per cell on the shared `.custom-timeline-navbar` node. At
 * 200 objects / 300 animations that meant ~6 800 listeners on a single node, and every
 * scroll event forced 6 800 layout reads — the main contributor to the high INP.
 *
 * Cell positions are deterministic and already known from props, so visibility is pure
 * arithmetic over the container's `scrollLeft`: one listener for the whole timeline and
 * zero layout reads per cell.
 *
 * Cells subscribe via `useSyncExternalStore`, so scrolling re-renders only the cells whose
 * visibility actually flipped. A React context would instead re-render every consumer on
 * every frame of the scroll.
 */

type ViewportMetrics = {
    /** horizontal scroll offset of the timeline container */
    scrollLeft: number
    /** visible width of the timeline container */
    clientWidth: number
}

/** extra px kept mounted on both sides so cells are ready before they scroll into view */
const OVERSCAN = 400

const initialMetrics = (): ViewportMetrics => ({
    scrollLeft: 0,
    // until the container is measured assume one screen — never zero, so a missing
    // container can not blank out the whole timeline
    clientWidth: typeof window === "undefined" ? 1920 : window.innerWidth,
})

let metrics: ViewportMetrics = initialMetrics()
let container: HTMLElement | null = null
const listeners = new Set<() => void>()

const subscribe = (listener: () => void) => {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

const publish = (next: ViewportMetrics) => {
    if (next.scrollLeft === metrics.scrollLeft && next.clientWidth === metrics.clientWidth) return
    metrics = next
    listeners.forEach((listener) => listener())
}

/** Latest known scroll state. Cached — reading it costs no layout. */
export const getTimelineViewportMetrics = (): Readonly<ViewportMetrics> => metrics

/** The scrollable timeline container, or null while the timeline is unmounted. */
export const getTimelineViewportElement = (): HTMLElement | null => container

/**
 * Scrolls the timeline horizontally and refreshes the cached metrics straight away, so
 * callers running every frame never act on a stale `scrollLeft`. The read-back is needed
 * because the browser clamps the assignment and a clamped write fires no scroll event.
 */
export const scrollTimelineTo = (scrollLeft: number) => {
    const element = container
    if (!element) return
    element.scrollLeft = scrollLeft
    publish({ scrollLeft: element.scrollLeft, clientWidth: metrics.clientWidth })
}

export const scrollTimelineBy = (delta: number) => {
    if (!delta) return
    scrollTimelineTo(metrics.scrollLeft + delta)
}

/**
 * Installs the single scroll/resize listener for the timeline. Call once, from the component
 * that owns the horizontally scrollable container.
 */
export const useTimelineViewportSource = (containerRef: RefObject<HTMLElement>) => {
    useEffect(() => {
        const element = containerRef.current
        if (!element) return

        container = element
        let rafId = 0

        const read = () => {
            rafId = 0
            publish({ scrollLeft: element.scrollLeft, clientWidth: element.clientWidth })
        }

        const onScroll = () => {
            if (rafId) return // at most one measurement per frame
            rafId = requestAnimationFrame(read)
        }

        read()
        element.addEventListener("scroll", onScroll, { passive: true })
        const observer = new ResizeObserver(onScroll)
        observer.observe(element)

        return () => {
            if (rafId) cancelAnimationFrame(rafId)
            element.removeEventListener("scroll", onScroll)
            observer.disconnect()
            container = null
            // reset without notifying: the subscribers are unmounting with us
            metrics = initialMetrics()
        }
    }, [containerRef])
}

/**
 * @param left  cell offset inside the timeline grid, in px
 * @param width cell width in px
 *
 * Fails open — non-finite geometry (duration 0, missing animation, …) keeps the cell
 * rendered instead of silently hiding content.
 */
export const useIsCellVisible = (left: number, width: number): boolean => {
    const getSnapshot = useCallback(() => {
        if (!Number.isFinite(left)) return true
        const safeWidth = Number.isFinite(width) && width > 0 ? width : 0
        // cells live in a grid that starts after the sticky row heading
        const from = metrics.scrollLeft - TIMELINE_ROW.headingWidth - OVERSCAN
        const to = metrics.scrollLeft + metrics.clientWidth - TIMELINE_ROW.headingWidth + OVERSCAN
        return left + safeWidth >= from && left <= to
    }, [left, width])

    return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}
